import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { format } from "date-fns";

interface Context {
  provider_name: string;
  provider_image: string | null;
  customer_name: string;
  session_date: string | null;
  already_used: boolean;
  expired: boolean;
}

const PublicReview = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState<Context | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "get-public-review-context",
          { body: { token } }
        );
        if (error) throw error;
        if ((data as any)?.error) {
          setLoadError((data as any).error);
        } else {
          setCtx(data as Context);
        }
      } catch (e: any) {
        setLoadError(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "submit-public-review",
        { body: { token, rating, comment } }
      );
      if (error) throw error;
      if ((data as any)?.error) {
        setSubmitError((data as any).error);
      } else {
        setSubmitted(true);
      }
    } catch (e: any) {
      setSubmitError(e.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Leave a Review | Wellsuites"
        description="Share your experience"
        noindex
      />
      <div className="min-h-screen bg-background flex flex-col">
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-lg">
            {loading ? (
              <div className="flex flex-col items-center gap-3 py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading…</p>
              </div>
            ) : loadError || !ctx ? (
              <Card>
                <CardContent className="pt-8 pb-8 text-center">
                  <AlertCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <h1 className="text-xl font-semibold mb-2">Link not valid</h1>
                  <p className="text-sm text-muted-foreground">
                    This review link is invalid or has expired.
                  </p>
                </CardContent>
              </Card>
            ) : ctx.already_used || submitted ? (
              <Card>
                <CardContent className="pt-8 pb-8 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-primary" />
                  <h1 className="text-2xl font-semibold mb-2">
                    {submitted ? "Thanks for your feedback!" : "Already submitted"}
                  </h1>
                  <p className="text-sm text-muted-foreground mb-6">
                    {submitted
                      ? `Your review for ${ctx.provider_name} has been recorded.`
                      : "You've already left a review for this booking."}
                  </p>
                  <Link to="/">
                    <Button variant="outline">Back to Wellsuites</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : ctx.expired ? (
              <Card>
                <CardContent className="pt-8 pb-8 text-center">
                  <AlertCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <h1 className="text-xl font-semibold mb-2">Link expired</h1>
                  <p className="text-sm text-muted-foreground">
                    This review link has expired.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-8 pb-8">
                  <div className="text-center mb-6">
                    {ctx.provider_image && (
                      <img
                        src={ctx.provider_image}
                        alt={ctx.provider_name}
                        className="w-16 h-16 rounded-full object-cover mx-auto mb-4"
                      />
                    )}
                    <h1 className="text-2xl font-semibold mb-1">
                      Hi {ctx.customer_name?.split(" ")[0] || "there"}!
                    </h1>
                    <p className="text-muted-foreground">
                      How was your session at <strong>{ctx.provider_name}</strong>
                      {ctx.session_date
                        ? ` on ${format(new Date(ctx.session_date), "MMM d, yyyy")}`
                        : ""}
                      ?
                    </p>
                  </div>

                  <div className="flex justify-center gap-1 mb-6">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        className="p-1 transition-transform hover:scale-110"
                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      >
                        <Star
                          className={`h-9 w-9 ${
                            (hover || rating) >= n
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-muted-foreground/40"
                          }`}
                        />
                      </button>
                    ))}
                  </div>

                  <Textarea
                    placeholder="Tell others about your experience (optional)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    maxLength={2000}
                    className="mb-4"
                  />

                  {submitError && (
                    <p className="text-sm text-destructive mb-3 text-center">
                      {submitError === "already_used"
                        ? "This review was already submitted."
                        : submitError === "expired"
                          ? "This link has expired."
                          : "Something went wrong. Please try again."}
                    </p>
                  )}

                  <Button
                    onClick={handleSubmit}
                    disabled={rating < 1 || submitting}
                    className="w-full"
                    size="lg"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : null}
                    Submit Review
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
        <footer className="py-6 text-center text-xs text-muted-foreground">
          Powered by{" "}
          <Link to="/" className="hover:underline">
            Wellsuites
          </Link>
        </footer>
      </div>
    </>
  );
};

export default PublicReview;
